/**
 * GroupRenderer - Renders grouped elements
 * Manages child elements with relative positions within a group container
 */

import Konva from 'konva';
import { BaseRenderer } from '../core/BaseRenderer';
import { EditableElement } from '../editing/types';
import { COLORS, SPACING, SHADOWS, SIZES } from '../utils/constants';

export class GroupRenderer extends BaseRenderer {
  private borderRect: Konva.Rect | null = null;
  private childRenderers: Map<string, { node: Konva.Group; element: EditableElement }> = new Map();
  private isEditMode: boolean = false;
  private _isSelectedState: boolean = false;

  constructor(element: EditableElement) {
    super(element);
  }

  /**
   * Render the group element
   */
  render(): Konva.Node {
    const { width, height, data } = this.element;
    
    // Group border (only visible in edit mode when selected)
    this.borderRect = new Konva.Rect({
      width: width,
      height: height,
      stroke: '#CCCCCC',
      strokeWidth: 1,
      dash: [10, 5],
      fill: 'transparent',
      listening: false,
      opacity: 0.5,
      visible: false,
    });
    this._node.add(this.borderRect);
    
    // Render children
    if (data?.children && Array.isArray(data.children)) {
      (data.children as EditableElement[]).forEach((child) => {
        const childNode = this.renderChildElement(child);
        if (childNode) {
          this._node.add(childNode);
          this.childRenderers.set(child.id, { node: childNode, element: child });
        }
      });
    }
    
    return this._node;
  }

  /**
   * Render a single child element
   */
  private renderChildElement(child: EditableElement): Konva.Group | null {
    const group = new Konva.Group({
      x: child.x,
      y: child.y,
    });
    
    switch (child.type) {
      case 'sticky':
        this.renderStickyChild(group, child);
        break;
      case 'text':
        this.renderTextChild(group, child);
        break;
      case 'shape':
        this.renderShapeChild(group, child);
        break;
      default:
        return null;
    }
    
    return group;
  }

  /**
   * Render sticky note child
   */
  private renderStickyChild(group: Konva.Group, child: EditableElement): void {
    const { width, height, data } = child;
    
    // Background
    const rect = new Konva.Rect({
      width: width,
      height: height,
      fill: data?.backgroundColor || COLORS.YELLOW,
      cornerRadius: data?.cornerRadius !== undefined ? data.cornerRadius : 0,
      shadowColor: data?.shadowColor || SHADOWS.STICKY_NOTE.color,
      shadowBlur: data?.shadowBlur || SHADOWS.STICKY_NOTE.blur,
      shadowOpacity: data?.shadowOpacity || SHADOWS.STICKY_NOTE.opacity,
      shadowOffset: {
        x: data?.shadowOffsetX || SHADOWS.STICKY_NOTE.offset.x,
        y: data?.shadowOffsetY || SHADOWS.STICKY_NOTE.offset.y,
      },
    });
    group.add(rect);
    
    // Text
    const textContent = (data?.text || 'Double-click to edit').trim() || 'Double-click to edit';
    const text = new Konva.Text({
      text: textContent,
      width: width,
      height: height,
      padding: data?.padding || SPACING.STICKY_PADDING,
      fontSize: data?.fontSize || 14,
      fontFamily: data?.fontFamily || 'Inter, sans-serif',
      fontStyle: data?.fontStyle || 'normal',
      fill: data?.fill || '#000000',
      align: data?.align || 'left',
      verticalAlign: 'top',
      wrap: 'word',
    });
    group.add(text);
  }

  /**
   * Render text child
   */
  private renderTextChild(group: Konva.Group, child: EditableElement): void {
    const { width, height, data } = child;
    
    const textContent = (data?.text || 'Double-click to edit').trim() || 'Double-click to edit';
    const text = new Konva.Text({
      text: textContent,
      width: width,
      height: height,
      fontSize: data?.fontSize || 16,
      fontFamily: data?.fontFamily || 'Arial',
      fontStyle: data?.fontStyle || 'normal',
      fill: data?.fill || '#000000',
      align: data?.align || 'left',
      verticalAlign: 'top',
      wrap: 'word',
    });
    group.add(text);
  }

  /**
   * Render shape child
   */
  private renderShapeChild(group: Konva.Group, child: EditableElement): void {
    const { width, height, data } = child;
    const shapeType = data?.shapeType || 'rectangle-rounded';
    
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) / 2;
    const cornerRadius = typeof data?.cornerRadius === 'number' 
      ? data.cornerRadius 
      : data?.cornerRadius?.uniform || 12;
    
    const baseProps = {
      fill: data?.fill || '#FFFFFF',
      stroke: data?.stroke || '#000000',
      strokeWidth: data?.strokeWidth || 2,
      opacity: data?.opacity ?? 1,
    };
    
    let shape: Konva.Shape;
    
    switch (shapeType) {
      case 'rectangle-rounded':
        shape = new Konva.Rect({
          width,
          height,
          cornerRadius,
          ...baseProps,
        });
        break;
      case 'rectangle-pill':
        const pillRadius = Math.min(width, height) / 2;
        shape = new Konva.Rect({
          width,
          height,
          cornerRadius: pillRadius,
          ...baseProps,
        });
        break;
      case 'circle':
        shape = new Konva.Circle({
          x: centerX,
          y: centerY,
          radius,
          ...baseProps,
        });
        break;
      case 'triangle-up':
        shape = new Konva.RegularPolygon({
          x: centerX,
          y: centerY,
          sides: 3,
          radius,
          rotation: -90,
          ...baseProps,
        });
        break;
      case 'triangle-down':
        shape = new Konva.RegularPolygon({
          x: centerX,
          y: centerY,
          sides: 3,
          radius,
          rotation: 90,
          ...baseProps,
        });
        break;
      case 'diamond':
        shape = new Konva.RegularPolygon({
          x: centerX,
          y: centerY,
          sides: 4,
          radius,
          rotation: 45,
          ...baseProps,
        });
        break;
      // Legacy shape types for backward compatibility
      case 'triangle':
        shape = new Konva.RegularPolygon({
          x: centerX,
          y: centerY,
          sides: 3,
          radius,
          rotation: -90,
          ...baseProps,
        });
        break;
      case 'star':
        shape = new Konva.Star({
          x: centerX,
          y: centerY,
          numPoints: 5,
          innerRadius: radius * 0.5,
          outerRadius: radius,
          ...baseProps,
        });
        break;
      case 'rectangle':
      default:
        // Legacy rectangle or fallback - use corner radius if provided, otherwise 0
        const rectCornerRadius = typeof data?.cornerRadius === 'number' 
          ? data.cornerRadius 
          : data?.cornerRadius?.uniform || 0;
        shape = new Konva.Rect({
          width,
          height,
          cornerRadius: rectCornerRadius,
          ...baseProps,
        });
        break;
    }
    
    group.add(shape);
    
    // Add text if present
    if (data?.text && data.text.trim()) {
      const text = new Konva.Text({
        text: data.text.trim(),
        width: width,
        height: height,
        fontSize: data?.fontSize || data?.textStyle?.fontSize || 16,
        fontFamily: data?.fontFamily || data?.textStyle?.fontFamily || 'Arial',
        fontStyle: data?.fontStyle || data?.textStyle?.fontStyle || 'normal',
        fill: data?.textFill || data?.textStyle?.fill || '#000000',
        align: data?.align || data?.textStyle?.align || 'center',
        verticalAlign: 'middle',
        padding: 8,
        wrap: 'word',
        lineHeight: data?.textStyle?.lineHeight,
        letterSpacing: data?.textStyle?.letterSpacing,
      });
      group.add(text);
    }
  }

  /**
   * Update the group with new properties
   */
  update(updates: Partial<EditableElement>): void {
    this.updateElementData(updates);
    
    const { width, height, data } = this.element;
    
    // Update border
    if (this.borderRect) {
      if (updates.width !== undefined || updates.height !== undefined) {
        this.borderRect.width(width);
        this.borderRect.height(height);
      }
    }
    
    // If children changed, rebuild
    if (updates.data?.children) {
      this.rebuildChildren();
    }
  }

  /**
   * Rebuild all children
   */
  private rebuildChildren(): void {
    // Remove old child nodes
    this.childRenderers.forEach(({ node }) => {
      node.destroy();
    });
    this.childRenderers.clear();
    
    // Recreate children
    const children = this.element.data?.children as EditableElement[] | undefined;
    if (children && Array.isArray(children)) {
      children.forEach((child) => {
        const childNode = this.renderChildElement(child);
        if (childNode) {
          this._node.add(childNode);
          this.childRenderers.set(child.id, { node: childNode, element: child });
        }
      });
    }
    
    // Make sure border is on top
    if (this.borderRect) {
      this.borderRect.moveToTop();
    }
  }

  /**
   * Set dimensions and update internal nodes
   */
  setDimensions(width: number, height: number): void {
    super.setDimensions(width, height);
    
    if (this.borderRect) {
      this.borderRect.width(width);
      this.borderRect.height(height);
    }
  }

  /**
   * Set edit mode (affects border visibility)
   */
  setEditMode(editMode: boolean): void {
    this.isEditMode = editMode;
    this.updateBorderVisibility();
  }

  /**
   * Set selected state (affects border visibility and style)
   */
  setSelected(selected: boolean): void {
    super.setSelected(selected);
    this._isSelectedState = selected;
    this.updateBorderVisibility();
    
    if (this.borderRect) {
      if (selected) {
        this.borderRect.stroke('#9C27B0');
        this.borderRect.strokeWidth(2);
        this.borderRect.opacity(1);
      } else {
        this.borderRect.stroke('#CCCCCC');
        this.borderRect.strokeWidth(1);
        this.borderRect.opacity(0.5);
      }
    }
  }

  /**
   * Update border visibility based on edit mode and selection
   */
  private updateBorderVisibility(): void {
    if (this.borderRect) {
      this.borderRect.visible(this.isEditMode);
    }
  }

  /**
   * Get child elements
   */
  getChildren(): EditableElement[] {
    return (this.element.data?.children as EditableElement[]) || [];
  }

  /**
   * Get child IDs
   */
  getChildIds(): string[] {
    return this.getChildren().map((child) => child.id);
  }

  /**
   * Destroy the renderer
   */
  destroy(): void {
    this.childRenderers.forEach(({ node }) => {
      node.destroy();
    });
    this.childRenderers.clear();
    this.borderRect = null;
    super.destroy();
  }
}
